import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiClient } from "./api";

/** How often the player bar's clock and waveform are allowed to re-render. */
const LABEL_INTERVAL_MS = 100;

/** `HTMLMediaElement.HAVE_METADATA` — the point at which a seek will be honoured. */
const HAVE_METADATA = 1;

export interface ReviewPlayer {
  playing: boolean;
  /** Seconds, updated ten times a second — for the clock and the waveform only. */
  time: number;
  /** Seconds. The recording's own duration when the Transcriber measured one. */
  duration: number;
  rate: number;
  /** Why nothing can be played, or null. */
  problem: string | null;
  toggle: () => void;
  pause: () => void;
  seek: (seconds: number) => void;
  nudge: (seconds: number) => void;
  setRate: (rate: number) => void;
  /**
   * Called on every animation frame while playing, with the audio element's own
   * `currentTime`. This is what drives the word highlight.
   *
   * A callback rather than state, because the highlight has to advance at frame rate
   * and re-rendering ten thousand word spans sixty times a second is not a thing that
   * can be made fast. The caller moves one class and one hairline instead.
   */
  onFrame: (handler: ((seconds: number) => void) | null) => void;
}

/**
 * UI-12's player: the whole recording, driven from SEC-4's signed link.
 *
 * Two rules from the M3 preparation note are the reason this is a hook and not a few
 * lines in the page.
 *
 * **The highlight is computed from `currentTime`, never from a running offset.** A
 * timer that adds 100 ms per tick drifts against the audio within a minute and is
 * visibly wrong within five — and an hour-long recording is the test case for every
 * screen in this milestone. Everything here reads the element.
 *
 * **The element is asked once and reused.** One signed link per visit, one `Audio` for
 * the page, and a seek that waits for `loadedmetadata` — setting `currentTime` before
 * any has loaded is silently discarded, which makes the first click of a visit play
 * the beginning of the meeting and every click after it play the right place.
 */
export function useReviewPlayer(
  api: ApiClient,
  recordingId: string | undefined,
  durationMs: number | null,
): ReviewPlayer {
  const audio = useRef<HTMLAudioElement | null>(null);
  const frame = useRef<((seconds: number) => void) | null>(null);
  const loop = useRef<number | null>(null);
  const lastLabel = useRef(0);
  // Set before metadata arrives and applied when it does — the whole reason a seek
  // made in the first moments of a visit is not thrown away.
  const pendingSeek = useRef<number | null>(null);

  const [source, setSource] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [rate, setRateState] = useState(1);
  const [measured, setMeasured] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);

  // A fresh link each time the screen is opened, per SEC-4 — which keeps the window in
  // which any given link is worth anything down to one visit.
  useEffect(() => {
    if (!recordingId) return;
    let cancelled = false;

    setSource(null);
    setProblem(null);

    void api
      .createAudioLink(recordingId)
      .then((link) => {
        if (!cancelled) setSource(link.url);
      })
      .catch(() => {
        if (!cancelled) setProblem("The audio for this recording could not be opened.");
      });

    return () => {
      cancelled = true;
    };
  }, [api, recordingId]);

  const element = useCallback((): HTMLAudioElement | null => {
    if (!source) return null;

    if (!audio.current) {
      const created = new Audio();
      created.preload = "metadata";
      created.addEventListener("loadedmetadata", () => {
        setMeasured(Number.isFinite(created.duration) ? created.duration : 0);
        if (pendingSeek.current !== null) {
          created.currentTime = pendingSeek.current;
          pendingSeek.current = null;
        }
      });
      created.addEventListener("ended", () => setPlaying(false));
      audio.current = created;
    }

    if (audio.current.src !== source) audio.current.src = source;

    return audio.current;
  }, [source]);

  // One loop for the whole screen, running only while there is sound. Every frame
  // hands the caller the element's own time; the clock and waveform get a tenth of
  // those, which is as often as a `mm:ss` label can change and more often than a
  // 96-bar track can show.
  useEffect(() => {
    if (!playing) return;

    let live = true;

    const tick = () => {
      if (!live) return;

      const current = audio.current?.currentTime ?? 0;
      frame.current?.(current);

      const now = performance.now();
      if (now - lastLabel.current >= LABEL_INTERVAL_MS) {
        lastLabel.current = now;
        setTime(current);
      }

      loop.current = requestAnimationFrame(tick);
    };

    loop.current = requestAnimationFrame(tick);

    return () => {
      live = false;
      if (loop.current !== null) cancelAnimationFrame(loop.current);
    };
  }, [playing]);

  // Leaving the screen has to stop the sound. Without this the element outlives the
  // component and keeps playing over whatever the owner navigated to.
  useEffect(
    () => () => {
      audio.current?.pause();
      audio.current = null;
    },
    [],
  );

  const seek = useCallback(
    (seconds: number) => {
      const target = Math.max(0, seconds);
      const media = element();

      setTime(target);
      frame.current?.(target);

      // No element yet means the signed link has not arrived; remember the seek so the
      // first click of a visit still plays the right place once it has.
      if (!media || media.readyState < HAVE_METADATA) {
        pendingSeek.current = target;
        return;
      }

      media.currentTime = target;
    },
    [element],
  );

  const pause = useCallback(() => {
    audio.current?.pause();
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    const media = element();
    if (!media) return;

    if (playing) {
      pause();
      return;
    }

    media.playbackRate = rate;
    setPlaying(true);

    // jsdom has no media stack and a browser may refuse playback; either way the
    // button must not be left claiming to play.
    void Promise.resolve(media.play()).catch(() => {
      setPlaying(false);
      setProblem("This browser would not play the recording.");
    });
  }, [element, pause, playing, rate]);

  const nudge = useCallback(
    (seconds: number) => seek((audio.current?.currentTime ?? time) + seconds),
    [seek, time],
  );

  const setRate = useCallback((next: number) => {
    setRateState(next);
    if (audio.current) audio.current.playbackRate = next;
  }, []);

  const onFrame = useCallback((handler: ((seconds: number) => void) | null) => {
    frame.current = handler;
  }, []);

  return {
    playing,
    time,
    // The Transcriber's number first: it measured the whole file, and it is there
    // before a single byte of audio has been fetched, so the track has its full length
    // from the first paint rather than growing under the cursor.
    duration: durationMs !== null && durationMs > 0 ? durationMs / 1000 : measured,
    rate,
    problem,
    toggle,
    pause,
    seek,
    nudge,
    setRate,
    onFrame,
  };
}
